import { Divider } from '@/components/layout/divider';
import { Stack } from '@/components/layout/stack';
import {
  Button,
  FormDateInput,
  FormSelectInput,
  Loading,
  SheetContent,
  SheetModal,
  SheetModalTrigger,
  showAlert,
  SvgIcon,
} from '@/components/ui';
import Text from '@/components/ui/text/text';
import { STRINGS } from '@/constants/strings';
import { useCoachQuery } from '@/features/home/queries';
import { LEVEL_OPTIONS } from '@/features/player/components/mutate-player/constants';
import { useStyles } from '@/features/player/components/player-profile/player-equipment/styles';
import {
  useCoachPlayerProfileQuery,
  useUpdateCoachPlayerMutation,
} from '@/features/player/queries';
import { useScaling } from '@/hooks';
import { captureException } from '@/libs/logger';
import { useSessionStore } from '@/stores';
import { colors } from '@/theme/colors';
import { Activity, useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { useShallow } from 'zustand/shallow';

export function AddNewKitSheetModal({
  children,
  profileId,
}: {
  children: React.ReactNode;
  profileId: string;
}) {
  const styles = useStyles();
  const user = useSessionStore(useShallow(s => s.user));
  const coach = useCoachQuery(user?.uid);
  const coachName = coach?.data?.firstname ?? '';
  const { data: player } = useCoachPlayerProfileQuery(profileId);

  const { mutateAsync: updateCoachPlayer } = useUpdateCoachPlayerMutation();
  const { scale, isTablet } = useScaling();
  const [selectedLevel, setSelectedLevel] = useState<number>(1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const close = () => {
    setOpen(false);
    setSelectedLevel(1);
    setSelectedDate(new Date());
  };

  const onSave = async () => {
    setLoading(true);
    try {
      if (!selectedDate) throw new Error('Missing welcome kit date');
      await updateCoachPlayer({
        playerId: profileId,
        data: {
          welcomeKit: [
            ...(player?.welcomeKit ?? []),
            {
              level: selectedLevel,
              date: selectedDate.toISOString(),
              coachName: coachName.charAt(0).toUpperCase() + coachName.slice(1),
            },
          ],
        },
      });
      showAlert({
        message: STRINGS.player.profile.equipment.addKitSuccessMessage,
      });
    } catch (error) {
      captureException(
        {
          error_payload: error,
        },
        { tags: { usk_log_type: 'on_save_add_kit_sheet_modal' } },
      );
    } finally {
      setOpen(false);
      setLoading(false);
      setSelectedLevel(1);
      setSelectedDate(new Date());
    }
  };

  return (
    <SheetModal
      open={open}
      onOpenChange={_open => {
        setOpen(_open);
        if (!_open) {
          setSelectedLevel(1);
          setSelectedDate(new Date());
        }
      }}>
      <SheetModalTrigger>{children}</SheetModalTrigger>
      <SheetContent
        tabletWidth={scale(375)}
        closeOnBackdropPress={false}
        style={styles.sheet}
        enableDragToClose={!loading}>
        <Activity mode={loading ? 'visible' : 'hidden'}>
          <Stack
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={1000}
            justify="center"
            align="center">
            <Stack
              direction="column"
              minWidth={scale(140)}
              paddingY={scale(16)}
              paddingX={scale(16)}
              borderRadius={scale(12)}
              backgroundColor={colors['Backgrounds/Primary']}>
              <Loading indicatorSize="small" />
            </Stack>
          </Stack>
        </Activity>
        <Stack direction="column" flex={1}>
          <Stack direction="row" align="center" style={styles.sheetHeader}>
            <TouchableOpacity onPress={close} hitSlop={scale(12)} style={styles.headerSide}>
              <Stack direction="row" align="center" gap={scale(2)}>
                <SvgIcon name="IcBackBlue" size={24} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Regular 12px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.headerActionText}>
                  {STRINGS.player.profile.header.back}
                </Text>
              </Stack>
            </TouchableOpacity>

            <Stack flex={1} align="center" justify="center">
              <Text
                variant={
                  isTablet
                    ? 'Font Inter/Subtitle/Bold 14px'
                    : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                }
                fontWeight={isTablet ? undefined : '800'}
                style={styles.sheetTitle}>
                {STRINGS.player.profile.equipment.addSheetTitle}
              </Text>
            </Stack>

            <Stack style={styles.headerSide} />
          </Stack>

          <Divider thickness={StyleSheet.hairlineWidth} />

          <Stack paddingX={scale(18)} paddingY={scale(20)} gap={scale(20)}>
            <FormSelectInput
              label={STRINGS.player.profile.equipment.kitLevelLabel}
              required={false}
              options={LEVEL_OPTIONS}
              value={`level_${selectedLevel}`}
              onValueChange={value => setSelectedLevel(Number(value.replace('level_', '')))}
              placeholder={STRINGS.player.profile.equipment.kitLevelPlaceholder}
              labelStyle={styles.formLabel}
              statusBarTranslucent
              labelProps={{
                variant: 'Font Montserrat/Subtitle/Minimize 1 Bold 11px',
              }}
            />
            <FormDateInput
              label={STRINGS.player.profile.equipment.dateProvidedLabel}
              required={false}
              value={selectedDate}
              onDateChange={setSelectedDate}
              defaultValue={new Date()}
              maximumDate={new Date()}
              labelStyle={styles.formLabel}
              labelProps={{
                variant: 'Font Montserrat/Subtitle/Minimize 1 Bold 11px',
              }}
            />
          </Stack>

          <Stack flex={1} />

          <Stack style={styles.addKitFooter} direction={isTablet ? 'row' : 'column'}>
            <Button onPress={onSave} style={styles.addKitButton}>
              <Button.Text variant="Font Inter/Subtitle/Regular" transform="uppercase">
                {STRINGS.player.profile.equipment.saveChanges}
              </Button.Text>
            </Button>
            <Button variant="outline" onPress={close} style={styles.addKitButton}>
              <Button.Text variant="Font Inter/Subtitle/Regular" transform="uppercase">
                {STRINGS.player.profile.equipment.cancel}
              </Button.Text>
            </Button>
          </Stack>
        </Stack>
      </SheetContent>
    </SheetModal>
  );
}
